import {DragData, FloorPlanItem, FloorplanTable} from "./types";
import {
    Box,
    ButtonBase,
    Drawer,
    Typography,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import BottomMenu from "./shared/BottomMenu";
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import ReadSidebar from "./read/ReadSidebar";
import ReadSidebarSearch from "./read/ReadSidebarSearch";
import {useSelector} from "react-redux";
import {
    CollisionDetection,
    DndContext,
    DragCancelEvent,
    DragOverlay,
    DragStartEvent,
    DropAnimation,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    createShapeElement,
    createTableGroup,
    createTextElement,
    createWallElement,
} from "./utils/fabric-factory";
import TableOverlayCardBody from "./read/TableOverlayCardBody";
import TableGroupsOverlay from "./read/TableGroupsOverlay";
import TableOverlayLayer from "./read/TableOverlayLayer";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import Timeframe from "./read/Timeframe";
import {restrictToWindowEdges, snapCenterToCursor} from "@dnd-kit/modifiers";
import {useReadStore} from "./hooks/useReadStore";
import {getFloorplanGuestCount, getStatusColors} from "./utils/reservation-utils";
import {
    isReservationOngoingAt,
    isReservationUpcomingAt,
    reservationIntersectsTimeframe,
    useTimeframe
} from "./hooks/useTimeframe";
import {useFloorplanPrint} from "./hooks/useFloorplanPrint";
import {useFloorplanContext} from "./hooks/useFloorplanContext";
import {FabricObject} from "fabric";
import {useFloorplanTemporalRefresh} from "./hooks/useFloorplanTemporalRefresh";
import {useAutoPanOnDrag} from "./hooks/useAutoPanOnDrag";
import {
    FloorplanTableObject,
    isFloorplanTableObject,
} from "./utils/fabric-table-object";
import {StoredTableGroup} from "./utils/table-groups";
import FloorplanPrintPreviewDialog from "./read/print/FloorplanPrintPreviewDialog";
import {useMobileResizableDrawer} from "./hooks/useMobileResizableDrawer";
import {useFloorplanLocate} from "./hooks/useFloorplanLocate";
import {fitCanvasToContainer} from "./hooks/useViewportControls";


interface Props {
    elements: FloorPlanItem[];
    tableGroups: StoredTableGroup[];
    desktopReservationsCollapsible?: boolean;
}

const DIALOG_CANCEL_DROP_ANIMATION: DropAnimation = {
    duration: 180,
    easing: "ease-out",
    keyframes: () => [
        {
            scale: "1",
            opacity: 1,
        },
        {
            scale: "0",
            opacity: 0,
        },
    ],
};

const ReadCanvasView: React.FC<Props> = ({
    elements,
    tableGroups,
    desktopReservationsCollapsible = false,
}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width:900px)');
    const searchBarOpenTriggered = useSelector((state: any) => state.action.searchBarTriggered);
    const {
        canvas,
        isInitialized,
        containerRef,
        canvasElRef,
        showSeatsAroundTable,
        restaurantName,
    } = useFloorplanContext();
    const hasTables = elements.some(item => !!item.table);
    const {previewOpen, previewData, cancelPreview} = useFloorplanPrint(tableGroups);
    const {
        drawerOpen,
        drawerHeight,
        isResizingDrawer,
        minHeight: mobileDrawerMinHeight,
        maxHeightVh: mobileDrawerMaxHeightVh,
        closeDrawer,
        handleOpenDrawer,
        handleDrawerResizeStart,
        resetDrawerHeight,
    } = useMobileResizableDrawer();
    const [desktopReservationsOpen, setDesktopReservationsOpen] = useState(true);

    const patch = useReadStore(s => s.patch);
    useFloorplanLocate();

    // Render floor plan items onto the canvas
    useEffect(() => {
        if (!canvas || !isInitialized) return;

        canvas.clear();
        canvas.selection = false;

        const tables: FloorplanTable[] = [];

        elements.forEach(item => {
            let fabricObject: FabricObject | null = null;

            if (item.table) {
                fabricObject = createTableGroup(item, showSeatsAroundTable, false, true);
            } else if (item.element) {
                switch (item.element.kind) {
                    case 'text':
                        fabricObject = createTextElement(item, false);
                        break;
                    case 'shape':
                        fabricObject = createShapeElement(item, false);
                        break;
                    case 'wall':
                        fabricObject = createWallElement(item, false);
                        break;
                }
            }

            if (fabricObject) {
                canvas.add(fabricObject);
                if (item.table?.resource?.id) {
                    tables.push({
                        id: item.table.resource.id,
                        resource: item.table.resource,
                        reservations: [],
                        fabricObject,
                    });
                }
            }
        });

        canvas.renderAll();
        patch({floorplanTables: tables})
        // console.log("Canvas initialized with tables:", tables);
    }, [canvas, isInitialized, elements, showSeatsAroundTable, patch]);

    const floorplanTables = useReadStore(s => s.floorplanTables);
    const assignedReservations = useReadStore(s => s.assignedReservations);
    const {
        timeframeStart,
        timeframeEnd,
        referenceTime,
        reservationDaySlice
    } = useTimeframe();
    const temporalTick = useFloorplanTemporalRefresh({
        enabled: reservationDaySlice === "now",
        reservations: assignedReservations,
    });

    // Sync seat colors based on reservation guest counts within the current timeframe
    useEffect(() => {
        if (!canvas || !floorplanTables.length) return;

        for (const table of floorplanTables) {
            const allTableReservations = assignedReservations
                .filter(r => r.tableResources?.some(tr => tr.id === table.id))
                .sort((a, b) => {
                    const aStart = a.restaurantReservation?.expectedStartDate
                        ? new Date(a.restaurantReservation.expectedStartDate).getTime() : 0;
                    const bStart = b.restaurantReservation?.expectedStartDate
                        ? new Date(b.restaurantReservation.expectedStartDate).getTime() : 0;
                    return aStart - bStart;
                });

            const searchList = allTableReservations.filter(r => reservationIntersectsTimeframe(
                r.restaurantReservation?.expectedStartDate,
                r.restaurantReservation?.expectedEndDate,
                timeframeStart,
                timeframeEnd,
            ));

            const ongoing = searchList.find(r => {
                return isReservationOngoingAt(
                    r.restaurantReservation?.expectedStartDate,
                    r.restaurantReservation?.expectedEndDate,
                    referenceTime,
                );
            });
            const current = ongoing ?? searchList.find(r => {
                return isReservationUpcomingAt(
                    r.restaurantReservation?.expectedStartDate,
                    referenceTime,
                );
            });

            let guestCount = getFloorplanGuestCount(current);
            const tableResources = current?.tableResources ?? [];
            if (tableResources.length > 1) {
                let remaining = guestCount;
                const tableIndex = tableResources.findIndex(tr => tr.id === table.id);
                for (let i = 0; i < tableIndex && i < tableResources.length; i++) {
                    const prevId = tableResources[i].id;
                    const prevCapacity = floorplanTables.find(ft => ft.id === prevId)?.resource.capacity
                        ?? tableResources[i].capacity ?? 0;
                    remaining -= Math.min(remaining, prevCapacity);
                }
                guestCount = remaining;
            }

            const tableObject = table.fabricObject as FloorplanTableObject;
            if (!isFloorplanTableObject(tableObject)) continue;

            tableObject.setOccupiedSeatCount(guestCount);

            if (showSeatsAroundTable && current) {
                const {borderColor, backgroundColor} = getStatusColors(current.restaurantReservation?.status);
                tableObject.setBodyAppearance(backgroundColor, borderColor);
                tableObject.setSeatAppearance(borderColor, borderColor);
            } else {
                tableObject.clearBodyAppearance();
                tableObject.clearSeatAppearance();
            }
        }

        canvas.renderAll();
    }, [canvas, floorplanTables, assignedReservations, timeframeStart, timeframeEnd, referenceTime, temporalTick, showSeatsAroundTable]);

    // DnD sensors & active drag state (for DragOverlay)
    const desktopSensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
        useSensor(TouchSensor, {activationConstraint: {delay: 100, tolerance: 8}}),
        useSensor(KeyboardSensor),
    );
    const mobileSensors = useSensors(
        useSensor(TouchSensor, {activationConstraint: {delay: 150, tolerance: 8}}),
    );
    // Read-only users may not assign reservations to tables via drag & drop. Registering no
    // sensors disables every floorplan drag at the source, so the slots can't even be picked up.
    const noSensors = useSensors();
    const readOnly = useReadOnly();
    const sensors = readOnly ? noSensors : (isMobile ? mobileSensors : desktopSensors);

    const [activeDrag, setActiveDrag] = useState<DragData | null>(null);
    const {commitPan} = useAutoPanOnDrag(!!activeDrag);
    const skipDropAnimRef = useRef(false);
    const useDialogCancelAnimationRef = useRef(false);
    const blurActiveElement = () => {
        const focusedElement = document.activeElement;
        if (focusedElement instanceof HTMLElement) {
            focusedElement.blur();
        }
    };

    useEffect(() => {
        if (!activeDrag) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                blurActiveElement();
                setActiveDrag(null);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [activeDrag]);

    useEffect(() => {
        if (!desktopReservationsCollapsible || isMobile || !canvas) return;

        const frameId = requestAnimationFrame(() => {
            const container = containerRef.current;
            if (container) fitCanvasToContainer(canvas, container);
        });
        return () => cancelAnimationFrame(frameId);
    }, [canvas, containerRef, desktopReservationsCollapsible, desktopReservationsOpen, isMobile]);


    // Custom collision detection that reads fresh DOM rects instead of
    // relying on @dnd-kit's cached rects (which go stale during auto-pan).
    const canvasCollision: CollisionDetection = ({
                                                     droppableContainers,
                                                     pointerCoordinates
                                                 }) => {
        if (!pointerCoordinates) return [];

        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect) return [];

        const {x, y} = pointerCoordinates;
        if (x < containerRect.left || x > containerRect.right || y < containerRect.top || y > containerRect.bottom) {
            return [];
        }

        const collisions = [];
        for (const droppable of droppableContainers) {
            const node = droppable.node.current;
            if (!node) continue;

            const rect = node.getBoundingClientRect();
            if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) continue;

            const dx = x - (rect.left + rect.width / 2);
            const dy = y - (rect.top + rect.height / 2);
            collisions.push({
                id: droppable.id,
                data: {droppableContainer: droppable, value: dx * dx + dy * dy}
            });
        }

        return collisions.sort((a, b) => a.data.value - b.data.value);
    };

    const dragZoomRef = useRef(1);
    const handleDragStart = (event: DragStartEvent) => {
        skipDropAnimRef.current = false;
        useDialogCancelAnimationRef.current = false;
        dragZoomRef.current = canvas?.viewportTransform?.[0] ?? 1;
        setActiveDrag(event.active.data.current as DragData);
        if (isMobile) closeDrawer();
    };
    const handleDragEnd = () => {
        useDialogCancelAnimationRef.current = false;
        blurActiveElement();
        commitPan(); // keep the auto-panned viewport on successful drop
        setActiveDrag(null);
    };
    const handleDragCancel = (event: DragCancelEvent) => {
        const dragData = event.active.data.current as DragData | undefined;
        useDialogCancelAnimationRef.current =
            dragData?.type === "assigned"
            && dragData.origin === "table-reservations-dialog";
        blurActiveElement();
        // don't commitPan — viewport restores to pre-pan position
        setActiveDrag(null);
    };

    return <DndContext
        sensors={sensors}
        collisionDetection={canvasCollision}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
    >
        <DragOverlay
            modifiers={[snapCenterToCursor, restrictToWindowEdges]}
            dropAnimation={
                skipDropAnimRef.current
                    ? null
                    : useDialogCancelAnimationRef.current
                        ? DIALOG_CANCEL_DROP_ANIMATION
                        : undefined
            }
            style={{cursor: "grabbing", zIndex: 9999}}
        >
            {activeDrag && (
                <div style={{
                    display: 'inline-block',
                    transform: `scale(${Math.max(0.6, dragZoomRef.current)})`
                }}>
                    {activeDrag.type === "unassigned" && (
                        <TableOverlayCardBody
                            reservation={activeDrag.reservation.restaurantReservation}
                            guestCounts={activeDrag.reservation.displayGuestCounts}
                            profiles={activeDrag.reservation.profiles}
                            accommodationReservations={activeDrag.reservation.accommodationReservations}
                            accommodationResources={activeDrag.reservation.accommodationResources}
                        />
                    )}
                    {activeDrag.type === "halfboard" && (
                        <TableOverlayCardBody
                            reservation={activeDrag.halfboard.accommodationReservation}
                            accommodationReservations={[activeDrag.halfboard.accommodationReservation]}
                            profiles={activeDrag.halfboard.profiles}
                            accommodationResources={activeDrag.halfboard.accommodationResources}
                            showDateFormat
                        />
                    )}
                    {activeDrag.type === "assigned" && (
                        <TableOverlayCardBody
                            reservation={activeDrag.reservation.restaurantReservation}
                            guestCounts={activeDrag.reservation.displayGuestCounts}
                            profiles={activeDrag.reservation.profiles}
                            accommodationResources={activeDrag.reservation.accommodationResources}
                        />
                    )}
                </div>
            )}
        </DragOverlay>

        <Box sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            bgcolor: 'rgba(249, 250, 252, 0.92)',
        }}>
            <Box sx={{
                flex: 1,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                minWidth: 0,
                overflow: 'hidden',
            }}>
                <Box
                    ref={containerRef}
                    sx={{
                        position: 'relative',
                        borderRadius: 0,
                        overflow: 'hidden',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <canvas ref={canvasElRef}
                            style={{position: 'relative', zIndex: 1}}/>

                    <TableGroupsOverlay groups={tableGroups}/>

                    {!hasTables && (
                        <Box sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 3,
                            pointerEvents: 'none',
                        }}>
                            <Box sx={{
                                color: 'text.secondary',
                                fontSize: 14,
                                textAlign: 'center',
                                px: 2,
                            }}>
                                {t('{{name}} has no tables and cannot show reservations', {name: restaurantName})}
                            </Box>
                        </Box>
                    )}

                    <Timeframe/>

                    {/* HTML overlays centered on each table */}
                    <TableOverlayLayer
                        onConflictWillOpen={() => {
                            skipDropAnimRef.current = true;
                        }}
                        disableTableCardDrag={isMobile && drawerOpen}
                    />

                    {desktopReservationsCollapsible && !isMobile && !desktopReservationsOpen && (
                        <ButtonBase
                            type="button"
                            aria-label={t("Show Reservations")}
                            onClick={() => setDesktopReservationsOpen(true)}
                            sx={{
                                position: "absolute",
                                top: 16,
                                right: 16,
                                zIndex: 20,
                                height: 34,
                                px: 1.25,
                                gap: 0.75,
                                display: "inline-flex",
                                alignItems: "center",
                                bgcolor: "background.paper",
                                color: "primary.main",
                                border: "1px solid",
                                borderColor: "primary.main",
                                borderRadius: 2,
                                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
                                "&:hover": {
                                    bgcolor: "action.hover",
                                },
                            }}
                        >
                            <MenuOpenIcon fontSize="small"/>
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {t("Show Reservations")}
                            </Typography>
                        </ButtonBase>
                    )}

                    {isInitialized && canvas && (
                        <BottomMenu
                            onOpenDrawer={isMobile ? handleOpenDrawer : undefined}
                            onHideReservations={desktopReservationsCollapsible && desktopReservationsOpen
                                ? () => setDesktopReservationsOpen(false)
                                : undefined}
                        />
                    )}
                </Box>

                {isMobile && (
                    <Drawer
                        variant="temporary"
                        anchor="bottom"
                        open={drawerOpen}
                        onClose={closeDrawer}
                        ModalProps={{keepMounted: true}}
                        SlideProps={{
                            onExited: resetDrawerHeight,
                        }}
                        PaperProps={{
                            sx: {
                                width: "100%",
                                height: drawerHeight,
                                minHeight: mobileDrawerMinHeight,
                                maxHeight: `${mobileDrawerMaxHeightVh * 100}vh`,
                                bgcolor: '#fff',
                                display: 'flex',
                                flexDirection: 'column',
                                borderTopLeftRadius: 16,
                                borderTopRightRadius: 16,
                                overflow: 'hidden',
                                transition: isResizingDrawer
                                    ? 'none'
                                    : theme.transitions.create('height', {
                                        duration: theme.transitions.duration.shortest,
                                    }),
                            },
                        }}
                    >
                        <Box
                            onPointerDown={handleDrawerResizeStart}
                            role="separator"
                            aria-label={t("Resize reservations panel")}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: 28,
                                cursor: 'ns-resize',
                                touchAction: 'none',
                                flexShrink: 0,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 42,
                                    height: 5,
                                    borderRadius: 999,
                                    bgcolor: 'rgba(0, 0, 0, 0.22)',
                                }}
                            />
                        </Box>

                        <Box sx={{flex: 1, minHeight: 0, overflow: 'auto'}}>
                            <Box sx={{position: 'relative', height: '100%'}}>
                                {searchBarOpenTriggered && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            zIndex: 10,
                                        }}
                                    >
                                        <ReadSidebarSearch/>
                                    </Box>
                                )}
                                <ReadSidebar onLocateReservation={closeDrawer}/>
                            </Box>
                        </Box>
                    </Drawer>
                )}
            </Box>

            {!isMobile && (!desktopReservationsCollapsible || desktopReservationsOpen) && (
                <Box sx={{
                    width: 340,
                    minWidth: 340,
                    flexShrink: 0,
                    position: 'relative'
                }}>
                    {searchBarOpenTriggered && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -60,
                                left: 0,
                                right: 0,
                                zIndex: 10,
                            }}
                        >
                            <ReadSidebarSearch/>
                        </Box>
                    )}
                    <Box
                        sx={{
                            height: '100%',
                            borderLeft: '1px solid',
                            borderColor: 'rgba(0, 0, 0, 0.08)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: '#fff',
                        }}
                    >
                        <ReadSidebar/>
                    </Box>
                </Box>
            )}

        </Box>

        <FloorplanPrintPreviewDialog
            open={previewOpen}
            previewData={previewData}
            onCancel={cancelPreview}
        />

    </DndContext>
}

export default ReadCanvasView;
